import React, { useState, useEffect, useCallback } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Plus, Trash2, User, Phone, Bike } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { getDeliveryPersons, createDeliveryPerson, deleteDeliveryPerson } from '../../services/taskApi';

const FlowDeliveryMasters = () => {
  const { isDark } = useOutletContext();
  const [persons, setPersons] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ name: '', phone: '', vehicle: '' });
  const [saving, setSaving] = useState(false);

  const cardCls = isDark ? 'bg-white/[0.04] border-white/10' : 'bg-white border-gray-200';
  const inputCls = isDark ? 'bg-white/5 border-white/10 text-white placeholder:text-white/30' : 'bg-white border-gray-300';

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getDeliveryPersons();
      setPersons(res.data || []);
    } catch { toast.error('Failed to load delivery persons'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    setSaving(true);
    try {
      await createDeliveryPerson(form);
      toast.success('Delivery person added');
      setForm({ name: '', phone: '', vehicle: '' });
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete delivery person "${p.name}"?`)) return;
    try {
      await deleteDeliveryPerson(p.id);
      toast.success('Deleted');
      setPersons(list => list.filter(x => x.id !== p.id));
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className={`text-2xl font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>Delivery Persons Master</h1>
          <p className={`text-sm mt-1 ${isDark ? 'text-white/50' : 'text-gray-500'}`}>Manage your in-house delivery boys & drivers. They appear in the dispatch picker.</p>
        </div>
        <div className={`text-xs px-3 py-1.5 rounded-full ${isDark ? 'bg-white/[0.04] text-white/60 border border-white/10' : 'bg-gray-100 text-gray-600 border border-gray-200'}`}>
          Total: <b>{persons.length}</b>
        </div>
      </div>

      {/* Add New */}
      <Card className={cardCls}>
        <CardContent className="p-4 sm:p-6">
          <form onSubmit={handleAdd} className="grid grid-cols-1 sm:grid-cols-4 gap-3 items-end">
            <div>
              <Label className={isDark ? 'text-white/60 text-xs' : 'text-xs'}>Name *</Label>
              <Input className={`mt-1 ${inputCls}`} placeholder="e.g. Ramesh Kumar"
                value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                data-testid="dp-name-input" />
            </div>
            <div>
              <Label className={isDark ? 'text-white/60 text-xs' : 'text-xs'}>Phone</Label>
              <Input className={`mt-1 ${inputCls}`} placeholder="+91 9876543210"
                value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                data-testid="dp-phone-input" />
            </div>
            <div>
              <Label className={isDark ? 'text-white/60 text-xs' : 'text-xs'}>Vehicle</Label>
              <Input className={`mt-1 ${inputCls}`} placeholder="e.g. KA01 AB 1234"
                value={form.vehicle} onChange={e => setForm(f => ({ ...f, vehicle: e.target.value }))}
                data-testid="dp-vehicle-input" />
            </div>
            <Button type="submit" disabled={saving} className="h-10 bg-blue-600 hover:bg-blue-700 disabled:opacity-50" data-testid="dp-add-btn">
              <Plus size={16} className="mr-1" /> {saving ? 'Adding…' : 'Add Person'}
            </Button>
          </form>
        </CardContent>
      </Card>

      {/* List */}
      <Card className={cardCls}>
        <CardContent className="p-0">
          {loading ? (
            <div className={`p-10 text-center text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>Loading…</div>
          ) : persons.length === 0 ? (
            <div className={`p-10 text-center text-sm ${isDark ? 'text-white/40' : 'text-gray-400'}`}>No delivery persons yet. Add one above to get started.</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className={isDark ? 'bg-white/[0.02]' : 'bg-gray-50'}>
                  <tr className={`text-[10px] uppercase tracking-wider ${isDark ? 'text-white/40' : 'text-gray-500'}`}>
                    <th className="text-left py-3 px-4 font-medium">Name</th>
                    <th className="text-left py-3 px-4 font-medium">Phone</th>
                    <th className="text-left py-3 px-4 font-medium">Vehicle</th>
                    <th className="text-left py-3 px-4 font-medium">Added</th>
                    <th className="text-right py-3 px-4 font-medium">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {persons.map((p) => (
                    <tr key={p.id} className={`border-t ${isDark ? 'border-white/5 hover:bg-white/[0.02]' : 'border-gray-100 hover:bg-gray-50'} transition-colors`} data-testid={`dp-row-${p.id}`}>
                      <td className="py-3 px-4">
                        <div className="flex items-center gap-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center ${isDark ? 'bg-blue-500/15 text-blue-300' : 'bg-blue-50 text-blue-600'}`}>
                            <User size={14} />
                          </div>
                          <span className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>{p.name}</span>
                        </div>
                      </td>
                      <td className={`py-3 px-4 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                        {p.phone ? <span className="inline-flex items-center gap-1"><Phone size={12} className="opacity-60" /> {p.phone}</span> : <span className={isDark ? 'text-white/30' : 'text-gray-300'}>—</span>}
                      </td>
                      <td className={`py-3 px-4 ${isDark ? 'text-white/70' : 'text-gray-700'}`}>
                        {p.vehicle ? <span className="inline-flex items-center gap-1"><Bike size={12} className="opacity-60" /> {p.vehicle}</span> : <span className={isDark ? 'text-white/30' : 'text-gray-300'}>—</span>}
                      </td>
                      <td className={`py-3 px-4 text-[11px] ${isDark ? 'text-white/40' : 'text-gray-400'}`}>
                        {p.created_at ? new Date(p.created_at).toLocaleDateString() : '—'}
                      </td>
                      <td className="py-3 px-4 text-right">
                        <Button size="sm" variant="ghost" className="h-8 px-2 text-red-400 hover:bg-red-500/10"
                          onClick={() => handleDelete(p)} data-testid={`dp-delete-${p.id}`}>
                          <Trash2 size={14} className="mr-1" /> Delete
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default FlowDeliveryMasters;
